import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, Trophy, CheckCircle2, Info } from "lucide-react";
import { api } from "@/lib/api";
import { MARKET_GROUPS, ALL_COLS, COL_LABEL } from "@/constants/giocataDemo";

// Griglia quote stile bookmaker collegata al backend.
// Una sola scelta per partita, fino a `max_events` selezioni totali.
export function GiocataGrid({ roomId, isAdmin = false, members = [], onSubmitted }) {
  const [grid, setGrid] = useState(null);
  const [sel, setSel] = useState({}); // matchId -> colKey
  const [target, setTarget] = useState(""); // admin: user_id per cui giocare ("" = me)
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = target ? `?on_behalf_of=${encodeURIComponent(target)}` : "";
      const g = await api(`/rooms/${roomId}/grid${qs}`);
      setGrid(g);
      setSel(g.selection || {});
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [roomId, target]);
  useEffect(() => { load(); }, [load]);

  const matches = grid?.matches || [];
  const maxEvents = grid?.max_events || 5;
  const locked = grid?.submissions_locked || grid?.settled;

  const count = Object.keys(sel).length;
  const total = useMemo(() => {
    let t = 1;
    for (const [mid, col] of Object.entries(sel)) {
      const mt = matches.find((m) => m.match_id === mid);
      const o = mt?.odds?.[col];
      if (o) t *= o;
    }
    return t;
  }, [sel, matches]);

  const pick = (mid, col) => {
    if (locked) return;
    setSel((prev) => {
      const cur = prev[mid];
      if (cur === col) { const { [mid]: _, ...rest } = prev; return rest; }
      if (!cur && Object.keys(prev).length >= maxEvents) {
        toast.error(`Massimo ${maxEvents} eventi per questa stanza.`);
        return prev;
      }
      return { ...prev, [mid]: col };
    });
  };

  const submit = async () => {
    if (count === 0) { toast.error("Seleziona almeno un evento."); return; }
    setBusy(true);
    try {
      const picks = Object.entries(sel).map(([match_id, col]) => ({ match_id, col }));
      const body = { picks };
      if (target) body.on_behalf_of = target;
      await api(`/rooms/${roomId}/grid/submit`, { method: "POST", body });
      toast.success(`Giocata confermata · ${count} eventi · quota ${total.toFixed(2)}`);
      await load();
      onSubmitted?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="rounded-xl border border-white/10 bg-[#181D22] p-6 text-center text-[#94A3B8]">Caricamento griglia...</div>;

  if (!grid?.available) {
    return (
      <div className="rounded-xl border border-white/10 bg-[#181D22] p-4 text-sm text-[#94A3B8] flex items-start gap-2" data-testid="grid-unavailable">
        <Info size={16} className="text-[#F59E0B] mt-0.5 shrink-0" />
        <span>{isAdmin ? "Nessuna quota impostata. Usa il pannello \"Gestione quote\" qui sotto per caricare la griglia della giornata." : "La griglia della giocata non è ancora disponibile: l'admin deve caricare le quote."}</span>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-[#181D22] overflow-hidden" data-testid="giocata-grid">
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/10 gap-2 flex-wrap">
        <div className="font-extrabold flex items-center gap-2"><Trophy size={16} className="text-[#F59E0B]" /> Fai la giocata</div>
        <div className="flex items-center gap-3">
          {isAdmin && members.length > 0 && (
            <select
              data-testid="grid-target-select"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="text-xs bg-[#0F1216] border border-white/15 rounded-md px-2 py-1 text-white"
            >
              <option value="">Gioca come: Me (admin)</option>
              {members.map((m) => <option key={m.user_id} value={m.user_id}>Per: {m.nickname}</option>)}
            </select>
          )}
          <div className="text-xs font-bold text-[#94A3B8]">Selezioni <span className={count >= maxEvents ? "text-[#EF4444]" : "text-[#F59E0B]"}>{count}/{maxEvents}</span></div>
        </div>
      </div>

      {locked && (
        <div className="px-4 py-2 text-xs font-bold text-[#F59E0B] bg-[#F59E0B]/10 flex items-center gap-1.5"><Lock size={13} /> Termine scaduto — giocata in sola lettura</div>
      )}

      <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
        <table className="w-full border-collapse text-sm min-w-[560px]">
          <thead>
            <tr className="bg-[#0F1216] text-[11px] uppercase tracking-wide text-[#94A3B8]">
              <th className="text-left px-2 py-2 sticky left-0 bg-[#0F1216] z-10">Partita</th>
              {MARKET_GROUPS.map((g) => (
                <th key={g.title} colSpan={g.cols.length} className="px-1 py-2 text-center border-l border-white/10">{g.title}</th>
              ))}
              <th className="w-3" aria-hidden />
            </tr>
            <tr className="bg-[#0F1216] text-xs text-[#64748B]">
              <th className="sticky left-0 bg-[#0F1216] z-10" />
              {MARKET_GROUPS.map((g) => g.cols.map((c, i) => (
                <th key={c} className={`px-1 py-1 text-center font-bold ${i === 0 ? "border-l border-white/10" : ""}`}>{c}</th>
              )))}
              <th className="w-3" aria-hidden />
            </tr>
          </thead>
          <tbody>
            {matches.map((m) => (
              <tr key={m.match_id} data-testid={`grid-row-${m.match_id}`} className="border-t border-white/5">
                <td className="px-2 py-2 sticky left-0 bg-[#181D22] z-10 min-w-[104px]">
                  <div className="text-[10px] text-[#64748B]">{m.date} · {m.time}</div>
                  <div className="font-bold leading-tight">{m.home}</div>
                  <div className="font-bold leading-tight text-[#94A3B8]">{m.away}</div>
                </td>
                {ALL_COLS.map((col) => {
                  const odd = m.odds?.[col];
                  const active = sel[m.match_id] === col;
                  const isGroupStart = MARKET_GROUPS.some((g) => g.cols[0] === col);
                  if (odd == null) {
                    return <td key={col} className={`px-1 py-1.5 text-center ${isGroupStart ? "border-l border-white/10" : ""}`}><div className="grid place-items-center h-8 rounded-md bg-white/5 text-[#475569]"><Lock size={12} /></div></td>;
                  }
                  return (
                    <td key={col} className={`px-1 py-1.5 ${isGroupStart ? "border-l border-white/10" : ""}`}>
                      <button
                        data-testid={`grid-cell-${m.match_id}-${col}`}
                        disabled={locked}
                        onClick={() => pick(m.match_id, col)}
                        className={`w-full min-w-[40px] h-8 rounded-md text-xs font-bold transition-colors disabled:cursor-not-allowed ${active ? "bg-[#F59E0B] text-[#1A1000]" : "bg-[#0F1216] text-white hover:bg-white/10 border border-white/10"}`}
                      >
                        {odd.toFixed(2)}
                      </button>
                    </td>
                  );
                })}
                <td className="w-3" aria-hidden />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {count > 0 && (
        <div className="border-t border-white/10 p-4 space-y-2 bg-[#0F1216]" data-testid="grid-slip">
          <div className="text-sm font-bold">La tua schedina</div>
          {Object.entries(sel).map(([mid, col]) => {
            const mt = matches.find((m) => m.match_id === mid);
            if (!mt) return null;
            return (
              <div key={mid} className="flex items-center justify-between text-sm">
                <span className="text-[#94A3B8] truncate">{mt.home}-{mt.away} · <b className="text-white">{COL_LABEL[col]}</b></span>
                <span className="text-[#F59E0B] font-bold">{mt.odds[col].toFixed(2)}</span>
              </div>
            );
          })}
          <div className="flex items-center justify-between pt-2 border-t border-white/10">
            <span className="text-sm font-bold">Quota totale</span>
            <span className="text-lg font-extrabold text-[#00D95F]" data-testid="grid-total">{total.toFixed(2)}</span>
          </div>
          {!locked && (
            <button
              data-testid="grid-confirm"
              onClick={submit}
              disabled={busy}
              className="w-full bg-[#F59E0B] text-[#1A1000] font-bold rounded-md py-2.5 mt-1 disabled:opacity-50"
            >
              {busy ? "Invio..." : `Conferma giocata (${count})`}
            </button>
          )}
        </div>
      )}
      {count === 0 && (grid.selection && Object.keys(grid.selection).length > 0) && (
        <div className="border-t border-white/10 px-4 py-2 text-xs text-[#00D95F] flex items-center gap-1.5"><CheckCircle2 size={13} /> Giocata già inviata (puoi modificarla fino alla scadenza)</div>
      )}
    </div>
  );
}
